import { ValueSemanticType } from '../atoms/Value';
import { TMutateOperator } from './api';
import type { TestConfig } from 'yup';

export interface IEntity {
  /**
   * Уникальное имя entity в пределах своей версии
   */
  id: string;

  /**
   * Источник данных для сущностий (например в какой БД и таблице ранятся записи)
   */
  storage: IEntityStorage;

  /**
   * Поля сущности
   */
  fields: IEntityField[];

  /**
   * Имя (исходное как в БД) колонки с первичным ключом.
   * Первичный ключ ен случайно может быть только одним полем.
   * Если он будет сдвоенный с ним невозможно работать стандартными средствами.
   * Ничего не мешает вам сделать сдвоенный ключ через уникальный индекс
   */
  primaryKeyFieldName: string;

  /**
   * Поле, где находится главное "название" или "имя" объекта.
   * Например для product - это название продукта,
   * для таблиц связи многие ко многим и пр. поле не заполняется.
   *
   * Почему так сложно сделать получение сдвоенного поля как имя и фамилия для юзера?
   * На это несколько причин:
   *  1. Надо вводить особую структуру для описания шаблонизации и
   *    списка выбираемых для нее данных
   *  2. При прохождении п1. мы попадаем на проблемы сортировки полученных данных
   *    при получении для options list api. И это очень серьезное ограничение,
   *    так как сортировать в коде бекенда или фронтенда попросту неэффективно +
   *    надо будет выгребать из БД все данные, сортировать и отрезать лишнее.
   *  3. Если для вывода title нам надо что-то налету считать в коде, то невозможно
   *    нормально искать по полю и сделать на него индекс в БД.
   *
   * Итого, как решать проблему вывода сложных комбинаций полей в заголовок объекта?
   *   Сделать в БД рассчитываемое по бд триггерам поле,
   *   также может подойти просто регулярный пересчет или пересчет на основе mutate в ng api.
   *   В любом случае это будет гораздо эффективнее на выборке, но, конечно, будет тратить место на хранение.
   */
  titleFieldName?: string;

  /**
   * если установлено, то в секцию where в сгенерированном запросе для entity
   * будет подставлено значение этого параметра
   */
  unconditionalWhereClause?: string;

  /**
   * Помоги коллегами и опиши тут все, что знаешь про эту сущность!
   * Добавь ссылки на всё, что может помочь новому члену команды.
   *
   * Описание entity, которе не будет прямо соответствовать названию,
   * а будет раскрывать суть. Например - для таблицы "agencies" нет смысла писать, что "тут храняться агентства".
   * Лучше указать ссылку на задачу или доку, которая поясняем что это и зачем, или расписать самому.
   */
  internalDescription: string;

  /**
   * Права доступа к сущности в целом - например разрешено в целом обращение к этой таблице или нет
   */
  acl: IEntityAcl;

  /**
   * Надо задать разрешенные операторы или не задать их, если вы делаете отчет.
   * Часто бывает, что удаление запрещено или для таблиц связки не нужен SET, так как связь добавляется и удаляется.
   */
  allowedMutateOperators: TMutateOperator[];

  options?: {
    // обязательный ключ для options
    default: {
      params?: [
        {
          name: string;
          dataType: string;
        },
      ]; // параметры для фильтрации
      sql: string; // кастомный select, обязательное поле
      fields?: [];
    };
    [key: string]: {
      params?: [
        {
          name: string;
          dataType: string;
        },
      ]; // параметры для фильтрации
      fields?: []; // список возвращаемых полей, необязательный параметр. Так как
      // можно при желании возвращать список кастомных полей.
      sql?: string; // можно при необходимости написать свой запрос на языке SQL, если
      // это поле отсутствует, будет использоваться функция generateOptionsSQL
    };
  };
}

/**
 * Здесь могут храниться в вите списока текстов как enum-ы ролей, так в будущем и enum-ы
 * разрешений или модификаторов ролей типа "centra.perm.dev", "centra.perm.allowViewAllClients".
 * Соответствено юзер будет получать на одну роль, а string[], в котором будет и текущая роль
 * и может быть набор дополнительных модификторов ролей, а которых написано выше.
 *
 * Еще примеры возможных модификаторов:
 *  - allUsers: авторизованные и неавторихзованные пользователи
 *  - authedUsers: только авторизованные пользователи
 */
export interface IEntityAcl {
  /**
   * Роли имеющие полный доступ на чтение и запись.
   * При этом позволяется чтение всего списка без учета unconditionalWhereClause
   */
  admin: string[];

  /**
   * Роли, имеющие доступ только на чтение с учетом unconditionalWhereClause
   */
  viewer: string[];

  /**
   * Роли имеющие доступ на создание, редактирование и удаление записей, которые
   * можно получить с доступов на чтение
   */
  editor: string[];
}

export enum EntityStorageType {
  /**
   * Хранение в таблице или view в БД
   */
  POSTGRESQL = 'POSTGRESQL',

  /**
   * Источником будет являться запрос, который будет написан в entity
   */
  POSTGRESQL_QUERY = 'POSTGRESQL_QUERY',
}

/**
 * Хранение данных сейчас толькл в PG.
 * Интерфейс нужен для более простого поиска вхождений в будущем
 */
export interface IEntityStorage {
  /**
   * Сейчас только POSTGRESQL
   */
  type: EntityStorageType;

  /**
   * Когда entity хранится в таблице
   */
  postgresql?: IEntityStoragePostgresql;

  /**
   * Когда entity является отчетом
   */
  postgresqlQuery?: IEntityStoragePostgresqlQuery;
}

export interface IEntityStoragePostgresql {
  // ссылка на креды будет как только будет несколько бд
  // connectionId: string;

  schemaName: string;
  tableName: string;
}

export interface IEntityStoragePostgresqlQuery {
  // ссылка на креды будет как только будет несколько бд
  // connectionId: string;

  sqlQuery: string;
}

/**
 * По мотивам. Решает проблему как в таблицчном виде
 * без сильных вложенных json-ов передать дополнительную
 * информацию к предыдущей data колонке.
 *
 * Почему не догенеритьвать json к колонке?
 * В БД это делать затратно по CPU и
 * не по всех БД это можно (последнее на всякий случай)
 *
 * @link https://developers.google.com/chart/interactive/docs/roles
 */
export type IEntityFieldRole =
  | 'data' // обычные данные - стандартная ситуация
  | 'entityForeignLink'; // Данные для построения ссылки

/**
 * Описывает конкретное полу сущности (таблицы)
 */
export interface IEntityField {
  name: string;

  /**
   * Тип данных для БД
   */
  dbType: string;

  /**
   * Указывает на то, что поле содержит массив данных типа dataType
   * Например для dbType: integer[] тут repeated=true, dataType=NUMBER
   */
  repeated?: boolean;

  /**
   * Тип данных с точки зрения системы.
   * Используется
   */
  dataType: DataType;

  /**
   * Семантическое расширение dataType для понимания того, что именно хранится в поле
   */
  semanticType: ValueSemanticType;

  /**
   * По умолчанию `data`.
   * Используются для обогащения вывода ближайшего
   * предыдущего столбца с role=data
   *
   * Идея отсюда:
   * @link https://developers.google.com/chart/interactive/docs/roles
   */
  role?: IEntityFieldRole;

  alias?: string;

  selectable?: boolean;

  /**
   * Поле автоматически генерирует содержимое
   * с помощью ng api или триггеров БД.
   *
   * Не поддерживает обновления и не должно выводиться в формах в виде инпутов
   */
  autofill?: boolean;

  sortable?: boolean;

  /**
   * Должно быть заполненно текстом-аргументацией депрекейта.
   * Если заполнено - означает, что мы хотим закончить работу с полем.
   * Оно больше не должно отображаться в автоадминке.
   * В дальнейшем обращение к этому полю будет регистрироваться как метрика для
   * автоматического отслеживания
   */
  deprecated?: string;

  /**
   * Ссылка на entity, объекты которой хранятся в этом поле.
   * Что-то вроде foreign key в БД
   */
  foreignEntityId?: string;

  /**
   * Ты поможешь коллегам, если будешь заполнять эту информацию,
   * но заполняй ее так, чтобы она не повторяла смысл названия поля.
   * Например, плохо - для поля name писать "Имя". Это будет только ухудшать читабельность.
   *
   * Хорошо будет заполнять спорные поля - например, если есть поля name и есть title. В этом
   * случае лучше подписать назвачение полей и, если есть, ссылки на доку или задачи по ним.
   */
  internalDescription?: string;

  // доступность инсерта или апдейта может быть переопределена ACL-ем по правам
  // тут базовые более простые настройки
  /**
   * Если false, то поле считается readonly и будет проигнорировано
   * при вставке данный через опрератор Operator.ADD
   */
  insertable?: boolean;

  /**
   * Если false, то поле считается readonly и будет проигнорировано
   * при вставке данный через опрератор Operator.SET
   */
  updatable?: boolean;

  /**
   * Валидаторы для поля
   */
  validators?: IEntityValidators;

  /**
   * Для типа RECORD
   * Обязательно описываем поля в json-е
   */
  fields?: IEntityField[];

  /**
   * Заполнение этого поля указывает на то, что оно хранит медиаданные.
   * Детальные типы хранимых данных указываются внутри этого поля.
   * Для всех таких полей генерируются url-ы загрузки данных и документация про параметры сжатия и валидации.
   */
  media?: IEntityFieldMediaSettings;

  /**
   * Значение по умолчанию
   */
  defaultValue?: string | number | boolean;
}

/**
 * На основе этих типов нужно составить список mime типов и/или расширений файлов
 */
export enum MediaType {
  /**
   * То, что сейчас мы считаем картинками - 'image/jpeg', 'image/png', 'image/gif'
   */
  IMAGE = 'IMAGE',

  /**
   * что угодно
   */
  ANY = 'ANY',
}

/**
 * Максимальное ограничение по высоте и ширине.
 * Изображение будет сжато по минимальной стороне.
 */
export interface IMediaImageResizeOption {
  width: number;
  height: number;
}

export interface IMediaImageSize {
  /**
   * Имя размера. Будет использоваться как ключ в ответе. Например "icon"
   */
  id: string;

  resize: IMediaImageResizeOption;
}

export interface IEntityFieldMediaSettings {
  /**
   * Разрешенные типы данных для этого поля
   */
  allowedTypes: MediaType[];

  /**
   * Максимальзый размер загружаемого файла в байтах
   */
  maxFileSizeInBytes?: number;

  imageSizes?: IMediaImageSize[];
}

/**
 * enum с бекенда (должен просто копироваться при изменениях)
 */
export enum DataType {
  STRING = 'STRING',
  NUMBER = 'NUMBER',
  BOOLEAN = 'BOOLEAN',

  /**
   * Говорит о том, что тут содержится объект - "{}"
   * Если на поле стоит параметр repeated: true, то это список объектов - "[]"
   */
  STRUCT = 'STRUCT',
}

export interface IEntityValidators {
  /**
   * Указание этих данных обязательно для валидации.
   * Для boolean, например, вообще будет возвращаться false по умолчанию
   * даже если форму не трогали и не предзаполняли
   */
  dataType: TDataType;

  /**
   * Если true, то в связке с dataType считает, что компонент возвращает списки, а не одно значение
   */
  repeated?: boolean;

  /**
   * Поле является обязательным для заполнения
   */
  required?: boolean;

  /**
   * Поле содержит email
   */
  email?: boolean;
  /**
   * Поле содержит телефон. Важно использовать в паре с маской и inputPhone, чтобы валидация отрабатывала корректно.
   */
  phone?: boolean;

  /**
   * Возможные варианты значения поля.
   * Входные данные будут проверяться по этому списку.
   */
  enums?: IEntityEnum[];

  /**
   * Для строк - минимальная длина строки, для чисел - минимальное число
   */
  min?: number;

  /**
   * Для строк - максимальная длина строки, для чисел - максимальное число
   */
  max?: number;

  /**
   * Для чисел - добавляет проверку на целое число
   */
  integer?: boolean;

  /**
   * Для чисел - добавляет проверку на число с двумя знаками после запятой
   */
  decimal?: boolean;

  /**
   * Для дополнительной валидации - проверяем значение с помощью функции
   */
  custom?: IEntityCustomValidation[];
}

/** Используем test из Yup валидации, со всеми параметрами можно ознакомиться в документации
 * @link https://github.com/jquense/yup#schematestoptions-object-schema */
export type IEntityCustomValidation = TestConfig;

export interface IEntityEnum {
  id: string;

  internalDescription?: string;
}

export type TDataType = keyof typeof DataType;
